import { z } from 'zod';

import { httpClient } from '@/services/http/http-client';
import type { Page } from '@/types/api.types';
import {
  type AccountWithPassword,
  accountWithPasswordSchema,
  type StaffRef,
  staffRefSchema,
  type UserAccount,
  userAccountSchema,
  type UserStatus,
} from '@/types/schemas';

import {
  type PermissionInfo,
  permissionInfoSchema,
  type Role,
  type RoleInput,
  roleSchema,
} from '../schemas/staff.schema';

/** The only place this feature talks to the network. */

// --- Staff accounts ---------------------------------------------------------------------

export interface StaffFilter {
  page: number;
  pageSize: number;
  q?: string | undefined;
  roleId?: number | undefined;
  status?: UserStatus | undefined;
}

export function listStaff(filter: StaffFilter, signal?: AbortSignal): Promise<Page<UserAccount>> {
  return httpClient.getPage('/users', { schema: userAccountSchema, query: { ...filter }, signal });
}

/**
 * Active staff, names only, for the "owner" pickers. Unlike `/users` this does not need
 * `users:manage` — anyone who can add a member or an enquiry has to be able to fill the field.
 */
export function listStaffOptions(signal?: AbortSignal): Promise<StaffRef[]> {
  return httpClient.get('/users/options', {
    schema: z.array(staffRefSchema),
    ...(signal ? { signal } : {}),
  });
}

export interface StaffInput {
  name: string;
  roleId: number;
  email?: string | null;
  mobile?: string | null;
  password?: string;
}

export function createStaff(input: StaffInput): Promise<AccountWithPassword> {
  return httpClient.post('/users', input, { schema: accountWithPasswordSchema });
}

export function updateStaff(
  id: number,
  input: Partial<Omit<StaffInput, 'password'>> & { status?: UserStatus },
): Promise<UserAccount> {
  return httpClient.patch(`/users/${String(id)}`, input, { schema: userAccountSchema });
}

export function resetStaffPassword(id: number): Promise<AccountWithPassword> {
  return httpClient.post(
    `/users/${String(id)}/reset-password`,
    {},
    { schema: accountWithPasswordSchema },
  );
}

// --- Roles ------------------------------------------------------------------------------

export function listRoles(signal?: AbortSignal): Promise<Role[]> {
  return httpClient.get('/roles', { schema: roleSchema.array(), ...(signal ? { signal } : {}) });
}

/** The permissions an admin can put in a role (member-only ones are left out by the API). */
export function listPermissions(signal?: AbortSignal): Promise<PermissionInfo[]> {
  return httpClient.get('/permissions', {
    schema: permissionInfoSchema.array(),
    ...(signal ? { signal } : {}),
  });
}

export function createRole(input: RoleInput): Promise<Role> {
  return httpClient.post('/roles', input, { schema: roleSchema });
}

export function updateRole(id: number, input: RoleInput): Promise<Role> {
  return httpClient.patch(`/roles/${String(id)}`, input, { schema: roleSchema });
}

export function deleteRole(id: number): Promise<void> {
  return httpClient.delete(`/roles/${String(id)}`);
}
